'use client';

import { useEffect, useRef, useCallback } from 'react';

import { captureAppException } from '@utils/exception-logger';
import { useClearDispatch } from '@contexts/auth/AuthContext';

import { getTabId } from './getTabId';

const AUTH_SYNC_CHANNEL_NAME = 'auth-sync-channel';

export enum AuthSyncType {
  LOGIN = 'LOGIN',
  LOGOUT = 'LOGOUT',
}

type AuthSyncMessage = {
  type: AuthSyncType;
  senderTabId: string;
  clearOptions?: {
    clearBasket?: boolean;
    clearQueryParams?: boolean;
  };
};

/**
 * Checks if the current URL path is one of the authentication routes
 * that should be redirected to home instead of reloaded when receiving auth sync messages.
 */
const isAuthRoute = (): boolean => {
  if (typeof window === 'undefined') return false;

  const pathname = window.location.pathname;
  const authRoutes = [
    '/giris-yap',
    '/uye-girisi/setur-extra',
    '/uye-ol/setur-extra',
  ];

  return authRoutes.includes(pathname);
};

/**
 * @description
 * Manages authentication state synchronization across multiple browser tabs using a BroadcastChannel.
 *
 * This hook performs two key roles:
 * 1. **Listening for Auth Events**: It listens for `LOGIN` and `LOGOUT` events broadcasted by other tabs.
 *    - If the current tab is inactive (`document.visibilityState === 'hidden'`), it takes action to sync the state.
 *    - For `LOGOUT` events, it clears user information via `useClearDispatch` before reloading the page.
 *    - For other events (like `LOGIN`), it simply reloads the page to get the new authentication state.
 *    - Active tabs ignore these events to avoid interrupting the user.
 *
 * 2. **Providing a Broadcast Function**: It returns a function, `broadcastAuthChange`, which allows the application
 *    to notify other tabs of an authentication state change.
 *
 * A singleton `tabId` is generated per tab to uniquely identify it and prevent a tab from acting on its own messages.
 *
 * @returns {{ broadcastAuthChange: (type: AuthSyncType, clearOptions?: { clearBasket?: boolean; clearQueryParams?: boolean }) => void }} An object containing the broadcast function.
 * - `broadcastAuthChange`: A memoized callback function to broadcast an authentication event (`LOGIN`, `LOGOUT`) to all other tabs with optional clear options.
 */
export const useAuthTabSync = () => {
  const tabIdRef = useRef(getTabId());
  const channel = useRef<BroadcastChannel | null>(null);
  const clearUserInformation = useClearDispatch();

  useEffect(() => {
    const currentTabId = tabIdRef.current;
    if (typeof window === 'undefined' || !currentTabId) {
      return;
    }

    try {
      if (!channel.current) {
        channel.current = new BroadcastChannel(AUTH_SYNC_CHANNEL_NAME);
      }
    } catch (error) {
      console.error(
        '[AuthTabSync] Broadcast Channel is not supported or failed to initialize.',
        error
      );
      captureAppException(error, 'AuthTabSyncChannelInit');
      return;
    }

    const handleMessage = async (event: MessageEvent<AuthSyncMessage>) => {
      const { senderTabId, type, clearOptions } = event.data;

      if (senderTabId === currentTabId) {
        return;
      }

      if (document.visibilityState === 'hidden') {
        if (type === AuthSyncType.LOGOUT) {
          await clearUserInformation({
            clearBasket: clearOptions?.clearBasket ?? true,
            clearQueryParams: clearOptions?.clearQueryParams ?? true,
          });
        }

        // If this is an auth route, redirect to home page instead of reloading
        // to prevent the auth component from mounting and clearing the session
        if (isAuthRoute()) {
          window.location.href = '/';
        } else {
          window.location.reload();
        }
      }
    };

    channel.current.addEventListener('message', handleMessage);

    return () => {
      channel.current?.removeEventListener('message', handleMessage);
      channel.current?.close();
      channel.current = null;
    };
  }, [clearUserInformation]);

  const broadcastAuthChange = useCallback(
    (
      type: AuthSyncType,
      clearOptions?: { clearBasket?: boolean; clearQueryParams?: boolean }
    ) => {
      const currentTabId = tabIdRef.current;
      if (!currentTabId) {
        return;
      }

      if (!channel.current) {
        return;
      }

      try {
        const message: AuthSyncMessage = {
          type,
          senderTabId: currentTabId,
          clearOptions,
        };
        channel.current.postMessage(message);
      } catch (error) {
        console.error(
          `[AuthTabSync] Failed to broadcast '${type}' event.`,
          error
        );
        captureAppException(error, 'AuthTabSyncBroadcast', { eventType: type });
      }
    },
    []
  );

  return { broadcastAuthChange };
};
